import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MascaraDirective } from '../../directives/mascara.directive';
import { TipoMascara } from '../../enums/tipo-mascara.enum';
import { MENSAGENS_PADRAO } from '../../constants/mensagens.const';
import { mensagemCampoObrigatorio } from '../../utils/validacao-mensagens.util';
import { LookupService } from '../../../core/services/lookup.service';
import { BotaoVoltarContextService } from '../../services/botao-voltar-context.service';

export type ModoFormulario = 'Inclusão' | 'Edição' | 'Visualização';
export type ColunaPrimeFlex = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
export type TipoCampoFormulario =
  | 'texto'
  | 'email'
  | 'senha'
  | 'cpf'
  | 'cnpj'
  | 'telefone'
  | 'cep'
  | 'textarea'
  | 'dropdown'
  | 'multiselecao'
  | 'autocomplete'
  | 'calendario'
  | 'intervalo-datas'
  | 'switch'
  | 'checkbox'
  | 'numero'
  | 'moeda'
  | 'status'
  | 'observacoes';

export interface OpcaoFormulario {
  label: string;
  value: unknown;
  disabled?: boolean;
  title?: string;
}

export interface CampoFormulario {
  chave: string;
  rotulo: string;
  tipo: TipoCampoFormulario;
  placeholder?: string;
  opcoes?: OpcaoFormulario[];
  sugestoes?: OpcaoFormulario[];
  aoBuscar?: (termo: string) => void;
  aoSelecionar?: (valor: unknown, form: FormGroup) => void;
  aoLimpar?: (form: FormGroup) => void;
  optionLabel?: string;
  optionValue?: string;
  optionDisabled?: string;
  filtro?: boolean;
  valorPadrao?: unknown;
  visivel?: boolean;
  desabilitado?: boolean;
  readonly?: boolean;
  permitirAbrirEmVisualizacao?: boolean;
  obrigatorio?: boolean;
  colSpan?: number;
  validadores?: ValidatorFn[];
  mascara?: string;
  mascaraTipo?: TipoMascara | string;
  lookupCampo?: string;
  ignorarLookup?: boolean;
  lookupLazy?: boolean;
  minimo?: number;
  maximo?: number;
  prefixo?: string;
  sufixo?: string;
  minFractionDigits?: number;
  maxFractionDigits?: number;
  permiteLimpar?: boolean;
  ajuda?: string;
}

export interface SecaoFormulario {
  titulo: string;
  descricao?: string;
  campos: CampoFormulario[];
  visivel?: boolean;
}

export interface AcaoFormulario {
  rotulo: string;
  icone: string;
  classe?: string;
  desabilitado?: boolean;
  aoClicar: () => void;
}

@Component({
  selector: 'app-formulario-padrao',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    MultiSelectModule,
    AutoCompleteModule,
    DatePickerModule,
    InputNumberModule,
    InputMaskModule,
    CheckboxModule,
    ToggleSwitchModule,
    MascaraDirective,
  ],
  templateUrl: './formulario-padrao.component.html',
  styleUrl: './formulario-padrao.component.scss',
})
export class FormularioPadraoComponent implements OnChanges, OnInit, OnDestroy {
  @Input() processando = false;
  @Input() mostrarSalvarContinuar = true;
  @Input() titulo = 'Cadastro';
  @Input() modo: ModoFormulario = 'Inclusão';
  @Input() campos: CampoFormulario[] = [];
  @Input() secoes: SecaoFormulario[] = [];
  @Input() formExterno?: FormGroup;
  @Input() mostrarAcoes = true;
  @Input() usarConteudoCustomizado = false;
  @Input() conteudoAposCampos = false;
  @Input() registrarBotaoVoltarTopbar = true;
  @Input() acoesPersonalizadas: AcaoFormulario[] = [];

  @Output() aoSalvar = new EventEmitter<Record<string, unknown>>();
  @Output() aoSalvarEContinuar = new EventEmitter<Record<string, unknown>>();
  @Output() aoCancelar = new EventEmitter<void>();
  @Output() aoVoltar = new EventEmitter<void>();

  protected form: FormGroup;
  protected sugestoesAutocomplete: Record<string, OpcaoFormulario[]> = {};
  private readonly lookupsCarregados = new Set<string>();
  private removerRegistroVoltar?: () => void;
  private assinaturaProjetoEtapas?: Subscription;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly lookupService: LookupService,
    private readonly botaoVoltarContext: BotaoVoltarContextService,
  ) {
    this.form = this.formBuilder.group({});
  }

  ngOnInit(): void {
    if (!this.registrarBotaoVoltarTopbar) {
      return;
    }

    this.removerRegistroVoltar = this.botaoVoltarContext.registrar(() => {
      if (!this.aoVoltar.observed) {
        return false;
      }

      this.aoVoltar.emit();
      return true;
    });
  }

  ngOnChanges(): void {
    this.reconstruirFormulario();
    this.preCarregarLookups();
    this.configurarRecargaEtapasPorProjeto();
  }

  ngOnDestroy(): void {
    this.removerRegistroVoltar?.();
    this.assinaturaProjetoEtapas?.unsubscribe();
  }

  protected obterSecoes(): SecaoFormulario[] {
    if (this.secoes?.length) {
      return this.secoes.filter((secao) => secao.visivel !== false);
    }

    return [
      {
        titulo: this.titulo,
        campos: this.campos,
      },
    ];
  }

  protected obterClasseCampo(campo: CampoFormulario): string {
    if (campo.tipo === 'textarea' || campo.tipo === 'observacoes') {
      return 'col-12';
    }

    const colSpan = campo.colSpan ?? 6;
    const lg = this.obterColunaDesktop(colSpan);
    const md = this.obterColunaTablet(lg);

    return `col-12 md:col-${md} lg:col-${lg}`;
  }

  protected isCampoObrigatorio(campo: CampoFormulario): boolean {
    if (campo.obrigatorio) {
      return true;
    }

    if (campo.validadores?.includes(Validators.required)) {
      return true;
    }

    const controle = this.form.get(campo.chave);
    return !!controle?.hasValidator?.(Validators.required);
  }

  protected salvar(): void {
    if (this.processando) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.aoSalvar.emit(this.form.getRawValue());
  }

  protected salvarEContinuar(): void {
    if (this.processando) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.aoSalvarEContinuar.emit(this.form.getRawValue());
  }

  protected buscarSugestoes(campo: CampoFormulario, termo: string): void {
    if (campo.aoBuscar) {
      campo.aoBuscar(termo);
      return;
    }

    if (!campo.opcoes) {
      this.sugestoesAutocomplete[campo.chave] = [];
      return;
    }

    const termoNormalizado = termo.toLowerCase();
    this.sugestoesAutocomplete[campo.chave] = campo.opcoes.filter((opcao) =>
      opcao.label.toLowerCase().includes(termoNormalizado),
    );
  }

  protected obterSugestoes(campo: CampoFormulario): OpcaoFormulario[] {
    return campo.sugestoes ?? this.sugestoesAutocomplete[campo.chave] ?? [];
  }

  protected aoSelecionarAutocomplete(campo: CampoFormulario, valor: unknown): void {
    campo.aoSelecionar?.(valor, this.form);
  }

  protected aoSelecionarOpcao(campo: CampoFormulario, valor: unknown): void {
    campo.aoSelecionar?.(valor, this.form);
  }

  protected aoLimparAutocomplete(campo: CampoFormulario): void {
    campo.aoLimpar?.(this.form);
  }

  protected isVisualizacao(): boolean {
    return this.modo === 'Visualização';
  }

  protected usarLookupLazy(campo: CampoFormulario): boolean {
    return (
      (campo.tipo === 'dropdown' || campo.tipo === 'status') &&
      campo.lookupLazy === true &&
      this.resolverCampoLookup(campo) !== null
    );
  }

  protected carregarLookupLazy(campo: CampoFormulario, event: { first?: number; rows?: number }): void {
    const campoLookup = this.resolverCampoLookup(campo);
    if (!campoLookup) {
      return;
    }
    const queryBase = this.lookupService.resolverPaginacaoLazy(event?.first ?? 0, event?.rows ?? 100);
    const query = this.montarQueryLookup(campoLookup, queryBase);
    if (!query) {
      campo.opcoes = [];
      return;
    }

    this.lookupService.listarPorCampo(campoLookup, query)?.subscribe({
      next: (resposta) => {
        const novasOpcoes = (resposta.data ?? []).map((item) => ({ label: item.nome, value: item.id }));
        const paginaInicial = query.pagina ?? 0;
        if (paginaInicial <= 0 || !campo.opcoes?.length) {
          campo.opcoes = novasOpcoes;
          return;
        }

        const combinadas = [...(campo.opcoes ?? [])];
        novasOpcoes.forEach((opcao) => {
          if (!combinadas.some((existente) => this.extrairValorOpcao(existente) === opcao.value)) {
            combinadas.push(opcao);
          }
        });
        campo.opcoes = combinadas;
      },
    });
  }

  protected obterMensagemErro(campo: CampoFormulario): string {
    const controle = this.form.get(campo.chave);
    if (!controle || !controle.errors) {
      return MENSAGENS_PADRAO.CAMPO_OBRIGATORIO;
    }

    if (controle.hasError('required')) {
      return mensagemCampoObrigatorio(campo.rotulo);
    }

    if (controle.hasError('email')) {
      return MENSAGENS_PADRAO.EMAIL_INVALIDO;
    }

    if (controle.hasError('cpfCnpjInvalido')) {
      return MENSAGENS_PADRAO.CPF_CNPJ_INVALIDO;
    }

    if (controle.hasError('cnpjInvalido')) {
      return MENSAGENS_PADRAO.CNPJ_INVALIDO;
    }

    if (controle.hasError('cpfInvalido')) {
      return MENSAGENS_PADRAO.CPF_INVALIDO;
    }

    if (controle.hasError('senhaDiferente')) {
      return MENSAGENS_PADRAO.SENHAS_NAO_CONFEREM;
    }

    if (controle.hasError('fimPlanejadoAntesInicio')) {
      return (
        (controle.getError('fimPlanejadoAntesInicio') as string | undefined) ??
        'O campo Fim Planejado não pode ser anterior ao campo Início Planejado.'
      );
    }

    return MENSAGENS_PADRAO.CAMPO_OBRIGATORIO;
  }

  protected isCampoCompetencia(campo: CampoFormulario): boolean {
    return campo.chave.toLowerCase().includes('datacompetencia');
  }

  protected obterFormatoData(campo: CampoFormulario): string {
    return this.isCampoCompetencia(campo) ? 'mm/yy' : 'dd/mm/yy';
  }

  protected obterVisaoData(campo: CampoFormulario): 'date' | 'month' {
    return this.isCampoCompetencia(campo) ? 'month' : 'date';
  }

  private reconstruirFormulario(): void {
    const campos = this.secoes?.length ? this.secoes.flatMap((secao) => secao.campos) : this.campos;
    const valoresAtuais = this.form ? this.form.getRawValue() : {};

    if (this.formExterno) {
      this.form = this.formExterno;
      campos.forEach((campo) => {
        if (campo.visivel === false) {
          return;
        }

        if (!this.form.get(campo.chave)) {
          const valorInicial = campo.valorPadrao ?? this.valorPadraoPorTipo(campo.tipo);
          this.form.addControl(
            campo.chave,
            this.formBuilder.control(valorInicial, campo.validadores ?? []),
          );
        }

        const deveDesabilitar = campo.desabilitado || campo.readonly || (this.isVisualizacao() && !campo.permitirAbrirEmVisualizacao);
        if (deveDesabilitar) {
          this.form.get(campo.chave)?.disable({ emitEvent: false });
        } else {
          this.form.get(campo.chave)?.enable({ emitEvent: false });
        }
      });
      return;
    }

    const grupo: Record<string, unknown> = {};
    campos.forEach((campo) => {
      if (campo.visivel === false) {
        return;
      }

      const valorExistente = valoresAtuais?.[campo.chave];
      const possuiValorPadrao = campo.valorPadrao !== undefined;
      const deveUsarValorPadrao =
        possuiValorPadrao &&
        (valorExistente === undefined || valorExistente === null || valorExistente === '');
      const valorInicial = deveUsarValorPadrao
        ? campo.valorPadrao
        : valorExistente !== undefined
          ? valorExistente
          : this.valorPadraoPorTipo(campo.tipo);
      grupo[campo.chave] = [valorInicial, campo.validadores ?? []];
    });

    this.form = this.formBuilder.group(grupo);
    campos.forEach((campo) => {
      const deveDesabilitar = campo.desabilitado || campo.readonly || (this.isVisualizacao() && !campo.permitirAbrirEmVisualizacao);
      if (deveDesabilitar) {
        this.form.get(campo.chave)?.disable({ emitEvent: false });
      } else {
        this.form.get(campo.chave)?.enable({ emitEvent: false });
      }
    });
  }

  private preCarregarLookups(): void {
    const campos = this.secoes?.length ? this.secoes.flatMap((secao) => secao.campos) : this.campos;
    campos
      .filter((campo) => (campo.tipo === 'dropdown' || campo.tipo === 'status') && this.resolverCampoLookup(campo) !== null)
      .forEach((campo) => {
        const campoLookup = this.resolverCampoLookup(campo);
        if (!campoLookup || this.lookupsCarregados.has(campo.chave)) {
          return;
        }
        this.lookupsCarregados.add(campo.chave);
        if (campo.lookupLazy) {
          return;
        }
        const query = this.montarQueryLookup(campoLookup, { pagina: 0, tamanho: 100 });
        if (!query) {
          campo.opcoes = [];
          return;
        }
        this.lookupService.listarPorCampo(campoLookup, query)?.subscribe({
          next: (resposta) => {
            campo.opcoes = (resposta.data ?? []).map((item) => ({ label: item.nome, value: item.id }));
          },
        });
      });
  }

  private configurarRecargaEtapasPorProjeto(): void {
    this.assinaturaProjetoEtapas?.unsubscribe();

    const controleProjeto = this.form.get('idProjeto');
    if (!controleProjeto) {
      return;
    }

    this.assinaturaProjetoEtapas = controleProjeto.valueChanges.subscribe(() => {
      this.atualizarOpcoesEtapasPorProjeto();
      this.atualizarOpcoesUnidadesPorProjeto();
    });
  }

  private atualizarOpcoesEtapasPorProjeto(): void {
    const campos = this.secoes?.length ? this.secoes.flatMap((secao) => secao.campos) : this.campos;
    const camposEtapa = campos.filter((campo) => this.resolverCampoLookup(campo) === 'etapas');
    const camposUnidade = campos.filter((campo) => this.resolverCampoLookup(campo) === 'unidades');

    if (!camposEtapa.length) {
      this.atualizarOpcoesUnidadesPorProjeto(camposUnidade);
      return;
    }

    const query = this.montarQueryLookup('etapas', { pagina: 0, tamanho: 100 });
    camposEtapa.forEach((campo) => {
      this.form.get(campo.chave)?.setValue(null, { emitEvent: false });

      if (!query) {
        campo.opcoes = [];
        return;
      }

      this.lookupService.listarPorCampo('etapas', query)?.subscribe({
        next: (resposta) => {
          campo.opcoes = (resposta.data ?? []).map((item) => ({ label: item.nome, value: item.id }));
        },
      });
    });

    this.atualizarOpcoesUnidadesPorProjeto(camposUnidade);
  }

  private atualizarOpcoesUnidadesPorProjeto(camposUnidade?: CampoFormulario[]): void {
    const campos = camposUnidade ?? (this.secoes?.length ? this.secoes.flatMap((secao) => secao.campos) : this.campos);
    const camposFiltrados = campos.filter((campo) => this.resolverCampoLookup(campo) === 'unidades');

    if (!camposFiltrados.length) {
      return;
    }

    camposFiltrados.forEach((campo) => {
      const chaveProjeto = campo.chave.endsWith('Destino') ? 'idProjetoDestino' : 'idProjeto';
      const idProjeto = Number(this.form.get(chaveProjeto)?.value);
      if (!Number.isFinite(idProjeto) || idProjeto <= 0) {
        campo.opcoes = [];
        this.form.get(campo.chave)?.setValue(null, { emitEvent: false });
        return;
      }

      const query = { pagina: 0, tamanho: 100, idProjeto };
      this.lookupService.listarPorCampo('unidades', query)?.subscribe({
        next: (resposta) => {
          campo.opcoes = (resposta.data ?? []).map((item) => ({ label: item.nome, value: item.id }));
        },
      });
    });
  }

  private montarQueryLookup(
    campoLookup: string,
    queryBase: { pagina?: number; tamanho?: number },
  ): { pagina?: number; tamanho?: number; idProjeto?: number } | null {
    if (campoLookup !== 'etapas' && campoLookup !== 'unidades') {
      return queryBase;
    }

    const idProjeto = Number(this.form.get('idProjeto')?.value);
    if (!Number.isFinite(idProjeto) || idProjeto <= 0) {
      return null;
    }

    return {
      ...queryBase,
      idProjeto,
    };
  }

  private resolverCampoLookup(campo: CampoFormulario): string | null {
    if (campo.ignorarLookup) {
      return null;
    }
    return campo.lookupCampo ?? campo.chave ?? null;
  }

  private valorPadraoPorTipo(tipo: TipoCampoFormulario): unknown {
    if (tipo === 'intervalo-datas') {
      return [];
    }

    if (tipo === 'checkbox' || tipo === 'switch') {
      return false;
    }

    return null;
  }

  private obterColunaDesktop(colSpan: number): ColunaPrimeFlex {
    const coluna = Math.max(1, Math.min(12, Math.round(colSpan)));
    return coluna as ColunaPrimeFlex;
  }

  private obterColunaTablet(colunaDesktop: number): ColunaPrimeFlex {
    const coluna = Math.max(1, Math.min(12, Math.round(colunaDesktop)));

    if (coluna >= 10) {
      return 12;
    }

    if (coluna >= 8) {
      return 10;
    }

    if (coluna >= 6) {
      return 8;
    }

    if (coluna >= 4) {
      return 6;
    }

    return 12;
  }

  private extrairValorOpcao(opcao: OpcaoFormulario | string | number): unknown {
    if (opcao && typeof opcao === 'object' && 'value' in opcao) {
      return (opcao as OpcaoFormulario).value;
    }
    return opcao;
  }
}
